/**
 * Rendered, editable markdown tables — the "sheets inside your note" piece.
 * When the cursor is outside a table, the raw pipes are replaced by a real
 * grid: click any cell and type (contentEditable), Tab/Enter to move,
 * + row / + column buttons, and a </> button to drop into raw markdown.
 * Every edit writes straight back to the markdown source, so tables stay
 * portable and sync like any other text.
 */
import { EditorSelection, EditorState, StateField, RangeSet } from "@codemirror/state";
import { Decoration, type DecorationSet, EditorView, WidgetType } from "@codemirror/view";
import { syntaxTree } from "@codemirror/language";
import type { SyntaxNode } from "@lezer/common";

export interface CellPos {
  from: number;
  to: number;
  text: string;
}
export interface TableData {
  from: number;
  to: number;
  aligns: ("left" | "center" | "right")[];
  /** first row is the header */
  rows: CellPos[][];
}

export function parseTable(state: EditorState, node: SyntaxNode): TableData | null {
  const doc = state.doc;
  const rows: CellPos[][] = [];
  const aligns: ("left" | "center" | "right")[] = [];

  for (let child = node.firstChild; child; child = child.nextSibling) {
    if (child.name === "TableDelimiter") {
      const raw = doc.sliceString(child.from, child.to);
      for (const seg of raw.split("|").map((s) => s.trim()).filter(Boolean)) {
        aligns.push(seg.startsWith(":") && seg.endsWith(":") ? "center" : seg.endsWith(":") ? "right" : "left");
      }
      continue;
    }
    if (child.name === "TableHeader" || child.name === "TableRow") {
      const cells: CellPos[] = [];
      for (let cell = child.firstChild; cell; cell = cell.nextSibling) {
        if (cell.name === "TableCell") {
          cells.push({ from: cell.from, to: cell.to, text: doc.sliceString(cell.from, cell.to) });
        }
      }
      // lezer omits empty cells as nodes; rebuild from raw pipes instead
      if (cells.length === 0 || countCols(doc.sliceString(child.from, child.to)) !== cells.length) {
        rows.push(cellsFromRaw(doc.sliceString(child.from, child.to), child.from));
      } else {
        rows.push(cells);
      }
    }
  }
  if (rows.length === 0) return null;
  return { from: node.from, to: node.to, aligns, rows };
}

function countCols(rawLine: string): number {
  return splitRow(rawLine).length;
}

function splitRow(rawLine: string): { text: string; start: number; end: number }[] {
  // split on unescaped pipes; track offsets
  const out: { text: string; start: number; end: number }[] = [];
  let seg = "";
  let segStart = 0;
  let i = 0;
  const line = rawLine;
  const push = (endIdx: number) => {
    out.push({ text: seg, start: segStart, end: endIdx });
  };
  // skip a single leading pipe
  if (line[0] === "|") {
    i = 1;
  }
  segStart = i;
  for (; i < line.length; i++) {
    const ch = line[i];
    if (ch === "\\" && line[i + 1] === "|") {
      seg += "\\|";
      i++;
      continue;
    }
    if (ch === "|") {
      push(i);
      seg = "";
      segStart = i + 1;
      continue;
    }
    seg += ch;
  }
  if (seg.trim().length > 0 || out.length === 0) push(line.length);
  else if (segStart < line.length) {
    // trailing content after last pipe was only whitespace; ignore
  }
  return out;
}

function cellsFromRaw(rawLine: string, lineFrom: number): CellPos[] {
  return splitRow(rawLine).map((seg) => {
    const trimmed = seg.text.trim();
    if (!trimmed) {
      // whitespace-only cell: target the whole segment (never inverted)
      return { from: lineFrom + seg.start, to: lineFrom + seg.end, text: "" };
    }
    const leading = seg.text.length - seg.text.trimStart().length;
    const trailing = seg.text.length - seg.text.trimEnd().length;
    return {
      from: lineFrom + seg.start + leading,
      to: lineFrom + seg.end - trailing,
      text: trimmed,
    };
  });
}

// focus restoration across widget rebuilds (commits destroy + recreate DOM)
let pendingFocus: { row: number; col: number; at: number } | null = null;
function takePendingFocus(): { row: number; col: number } | null {
  if (pendingFocus && Date.now() - pendingFocus.at < 500) {
    const f = pendingFocus;
    pendingFocus = null;
    return f;
  }
  pendingFocus = null;
  return null;
}

function escapeCell(text: string): string {
  // cells display unescaped text, so every pipe in textContent is literal
  return text.replace(/\n/g, " ").replace(/\|/g, "\\|").trim();
}

export class TableWidget extends WidgetType {
  private signature: string;

  constructor(readonly data: TableData) {
    super();
    this.signature = JSON.stringify(data.rows.map((r) => r.map((c) => c.text))) + data.aligns.join(",");
  }

  override eq(other: TableWidget) {
    return other.signature === this.signature && other.data.from === this.data.from;
  }

  override get estimatedHeight() {
    return this.data.rows.length * 32 + 30;
  }

  toDOM(view: EditorView) {
    const { data } = this;
    const wrap = document.createElement("div");
    wrap.className = "hn-table";

    const scroll = document.createElement("div");
    scroll.className = "hn-table-scroll";
    const table = document.createElement("table");

    // a cell may only ever commit ONCE per widget instance — after a commit
    // the doc changes, this widget is stale, and its positions are wrong
    const committed = new WeakSet<HTMLElement>();
    const commitCell = (cell: CellPos, el: HTMLElement) => {
      if (committed.has(el)) return false;
      const next = escapeCell(el.textContent ?? "");
      if (next === cell.text.trim()) return false;
      committed.add(el);
      queueMicrotask(() => {
        view.dispatch({ changes: { from: cell.from, to: cell.to, insert: ` ${next} ` } });
      });
      return true;
    };

    const focusCell = (row: number, col: number) => {
      const el = table.querySelector<HTMLElement>(`[data-r="${row}"][data-c="${col}"]`);
      if (el) {
        el.focus();
        // place caret at end
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
        return true;
      }
      return false;
    };

    data.rows.forEach((cells, rowIdx) => {
      const tr = document.createElement("tr");
      cells.forEach((cell, colIdx) => {
        const el = document.createElement(rowIdx === 0 ? "th" : "td");
        el.contentEditable = "true";
        el.spellcheck = false;
        el.dataset["r"] = String(rowIdx);
        el.dataset["c"] = String(colIdx);
        el.style.textAlign = data.aligns[colIdx] ?? "left";
        el.textContent = cell.text.replace(/\\\|/g, "|").trim();

        el.addEventListener("blur", () => {
          commitCell(cell, el);
        });
        el.addEventListener("keydown", (e) => {
          if (e.key === "Tab") {
            e.preventDefault();
            const dir = e.shiftKey ? -1 : 1;
            let r = rowIdx;
            let c = colIdx + dir;
            const cols = data.rows[0]!.length;
            if (c >= cols) {
              r++;
              c = 0;
            } else if (c < 0) {
              r--;
              c = cols - 1;
            }
            const changed = commitCell(cell, el);
            if (r >= data.rows.length) {
              pendingFocus = { row: r, col: 0, at: Date.now() };
              this.addRow(view, changed);
              return;
            }
            if (r < 0) return;
            if (changed) pendingFocus = { row: r, col: c, at: Date.now() };
            else focusCell(r, c);
          } else if (e.key === "Enter") {
            e.preventDefault();
            const r = rowIdx + 1;
            const changed = commitCell(cell, el);
            if (r >= data.rows.length) {
              pendingFocus = { row: r, col: colIdx, at: Date.now() };
              this.addRow(view, changed);
              return;
            }
            if (changed) pendingFocus = { row: r, col: colIdx, at: Date.now() };
            else focusCell(r, colIdx);
          } else if (e.key === "Escape") {
            e.preventDefault();
            (e.target as HTMLElement).blur();
            view.focus();
          }
        });
        tr.appendChild(el);
      });
      table.appendChild(tr);
    });

    scroll.appendChild(table);
    wrap.appendChild(scroll);

    // ---- toolbar: + row, + column, raw source ----
    const bar = document.createElement("div");
    bar.className = "hn-table-bar";
    const addRowBtn = document.createElement("button");
    addRowBtn.type = "button";
    addRowBtn.textContent = "+ Row";
    addRowBtn.addEventListener("mousedown", (e) => {
      e.preventDefault();
      pendingFocus = { row: data.rows.length, col: 0, at: Date.now() };
      this.addRow(view, false);
    });
    const addColBtn = document.createElement("button");
    addColBtn.type = "button";
    addColBtn.textContent = "+ Column";
    addColBtn.addEventListener("mousedown", (e) => {
      e.preventDefault();
      this.addColumn(view);
    });
    const srcBtn = document.createElement("button");
    srcBtn.type = "button";
    srcBtn.textContent = "</>";
    srcBtn.title = "Edit raw markdown";
    srcBtn.addEventListener("mousedown", (e) => {
      e.preventDefault();
      view.dispatch({ selection: EditorSelection.cursor(data.from), scrollIntoView: true });
      view.focus();
    });
    bar.append(addRowBtn, addColBtn, srcBtn);
    wrap.appendChild(bar);

    const restore = takePendingFocus();
    if (restore) {
      requestAnimationFrame(() => focusCell(restore.row, restore.col));
    }

    return wrap;
  }

  private cols(): number {
    return Math.max(...this.data.rows.map((r) => r.length), this.data.aligns.length, 1);
  }

  private addRow(view: EditorView, _afterPendingCommit: boolean) {
    const cols = this.cols();
    const row = `\n|${"   |".repeat(cols)}`;
    queueMicrotask(() => {
      view.dispatch({ changes: { from: view.state.doc.lineAt(this.data.to).to, insert: row } });
    });
  }

  private addColumn(view: EditorView) {
    // rebuild the whole table with one extra column — simple and correct
    const cols = this.cols();
    const aligns = [...this.data.aligns];
    while (aligns.length < cols) aligns.push("left");
    aligns.push("left");
    const lines: string[] = [];
    this.data.rows.forEach((cells, idx) => {
      const texts = cells.map((c) => c.text.trim() || " ");
      while (texts.length < cols) texts.push(" ");
      texts.push(" ");
      lines.push(`| ${texts.join(" | ")} |`);
      if (idx === 0) {
        lines.push(`| ${aligns.map((a) => (a === "center" ? ":---:" : a === "right" ? "---:" : "---")).join(" | ")} |`);
      }
    });
    queueMicrotask(() => {
      view.dispatch({ changes: { from: this.data.from, to: this.data.to, insert: lines.join("\n") } });
    });
  }

  override ignoreEvent() {
    return true;
  }
}

/**
 * Block-level replace decorations MUST come from a StateField (CodeMirror
 * rejects them from view plugins), so tables get their own field instead of
 * living in the live-preview plugin.
 */
function buildTableDecorations(state: EditorState): DecorationSet {
  const decos: ReturnType<Decoration["range"]>[] = [];
  syntaxTree(state).iterate({
    enter: (node) => {
      if (node.name !== "Table") return;
      const touched = state.selection.ranges.some((r) => r.from <= node.to && r.to >= node.from);
      if (touched) return false;
      const data = parseTable(state, node.node);
      if (data) {
        decos.push(Decoration.replace({ widget: new TableWidget(data), block: true }).range(node.from, node.to));
      }
      return false;
    },
  });
  return Decoration.set(decos, true);
}

export const tableField = StateField.define<DecorationSet>({
  create: buildTableDecorations,
  update(value, tr) {
    if (tr.docChanged || tr.selection || syntaxTree(tr.state) !== syntaxTree(tr.startState)) {
      return buildTableDecorations(tr.state);
    }
    return value;
  },
  provide: (f) => [
    EditorView.decorations.from(f),
    EditorView.atomicRanges.of((view) => view.state.field(f, false) ?? RangeSet.empty),
  ],
});

export const tableStyles = EditorView.theme({
  ".hn-table": {
    margin: "6px 0",
  },
  ".hn-table-scroll": {
    overflowX: "auto",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-strong)",
  },
  ".hn-table table": {
    borderCollapse: "collapse",
    width: "100%",
    fontSize: "0.95em",
    lineHeight: "1.5",
  },
  ".hn-table th, .hn-table td": {
    border: "1px solid var(--border)",
    padding: "5px 10px",
    minWidth: "60px",
    outline: "none",
    verticalAlign: "top",
  },
  ".hn-table tr:first-child th, .hn-table tr:first-child td": {
    borderTop: "none",
  },
  ".hn-table tr:last-child th, .hn-table tr:last-child td": {
    borderBottom: "none",
  },
  ".hn-table th:first-child, .hn-table td:first-child": {
    borderLeft: "none",
  },
  ".hn-table th:last-child, .hn-table td:last-child": {
    borderRight: "none",
  },
  ".hn-table th": {
    background: "var(--surface-panel)",
    fontWeight: "650",
    color: "var(--md-heading)",
  },
  ".hn-table td:focus, .hn-table th:focus": {
    boxShadow: "inset 0 0 0 1.5px var(--accent)",
    background: "var(--accent-subtle)",
  },
  ".hn-table tr:hover td": {
    background: "var(--surface-hover)",
  },
  ".hn-table tr:hover td:focus": {
    background: "var(--accent-subtle)",
  },
  ".hn-table-bar": {
    display: "flex",
    gap: "6px",
    paddingTop: "4px",
    opacity: "0",
    transition: "opacity 80ms",
  },
  ".hn-table:hover .hn-table-bar, .hn-table:focus-within .hn-table-bar": {
    opacity: "1",
  },
  ".hn-table-bar button": {
    fontSize: "10px",
    fontFamily: "var(--font-ui)",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--text-muted)",
    background: "transparent",
    border: "1px solid var(--border-strong)",
    borderRadius: "3px",
    padding: "2px 8px",
    cursor: "pointer",
  },
  ".hn-table-bar button:hover": {
    color: "var(--text-primary)",
    borderColor: "var(--accent)",
  },
});
